import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import Icon from "@material-ui/core/Icon";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import { dpBaseRoutes } from "base-routes";
// Reuse the Facility Admin "Facility Settings" modal so the edit dialog is pixel-identical
// across dashboards (same flat FacilitySettingsDTO + the same settings endpoints).
import FacilitySettings from "views/facilityAdmin/FacilitySettings";

/**
 * Org Admin landing view (frontend doc §5): the facility list of the caller's org.
 * Data: GET /api/org-admin/facilities (org resolved server-side from the caller's
 * ROLE_ORG_ADMIN row). Row actions:
 *   - Edit settings  -> themed modal hitting the guarded facility-admin settings
 *                       endpoints with the row's facilityId (org-over-org guard).
 *   - Open dashboard -> opens the DP dashboard in a new tab (decision #4).
 *
 * Endpoints used (unchanged from prior implementation):
 *  - GET  org-admin/facilities[?search=]                    Facility[]
 *  - GET  facility-admin/facilities/{fid}/settings          FacilitySettingsDTO (flat)
 *  - PUT  facility-admin/facilities/{fid}/settings          FacilitySettingsDTO (flat)
 */

const headCells = [
  { id: "facility", label: "Facility" },
  { id: "address", label: "Address" },
  { id: "status", label: "Status" },
  { id: "activeUsers", label: "Active Users" },
  { id: "action", label: "Actions" },
];

class OrgFacilityList extends React.Component {
  constructor(props) {
    super(props);
    let spinner = document.getElementById("loadingSpinner");
    this.state = {
      loading: false,
      spinner: spinner,
      rows: [],
      searchKeyword: "",
      // edit-settings dialog (rendered by the reused <FacilitySettings/> component)
      modalStatus: false,
      editingFacilityId: null,
    };
    this.search = this.search.bind(this);
  }

  componentDidMount() {
    this.fetchFacilities();
  }

  fetchFacilities(searchKeyword) {
    if (this.state.spinner) this.state.spinner.removeAttribute("hidden", "true");
    const q = searchKeyword ? `?search=${encodeURIComponent(searchKeyword)}` : "";
    fetch(`${apiPath.orgAdminFacilities}${q}`, {
      method: "GET",
      headers: generalAuthTokenHeader(),
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => this.setState({ rows: Array.isArray(data) ? data : [] }))
      .catch(() => this.setState({ rows: [] }))
      .finally(() => {
        if (this.state.spinner) this.state.spinner.setAttribute("hidden", "true");
      });
  }

  search(event) {
    let searchKeyword = event.target.value.trim();
    this.setState({ searchKeyword: searchKeyword });
    if (searchKeyword.length > 0) {
      this.fetchFacilities(searchKeyword);
    } else {
      this.fetchFacilities();
    }
    return true;
  }

  // ----- edit-settings dialog (delegated to the reused <FacilitySettings/> component) -----
  modalOpen = (facilityId) => {
    this.setState({ editingFacilityId: facilityId, modalStatus: true });
  };

  modalClose = () => {
    this.setState({ modalStatus: false });
  };

  // ----- open dashboard -----
  openDashboard = (f) => {
    // Opens the DP dashboard in a new tab, carrying the facility context the Org Admin
    // has no dischargeplanner row for: facilityId (scopes data) + facilityCode (drives
    // the VENDOR-vs-MAXMRJ facilityType). The backend guards the facilityId.
    const code = f.facilityCode
      ? `&facilityCode=${encodeURIComponent(f.facilityCode)}`
      : "";
    const name = f.facilityName
      ? `&facilityName=${encodeURIComponent(f.facilityName)}`
      : "";
    window.open(
      `${dpBaseRoutes.dashboard.path}?facilityId=${f.facilityId}${code}${name}`,
      "_blank"
    );
  };

  render() {
    return (
      <div id="org-facility-list-index">
        {/* Edit facility settings — reuses the Facility Admin modal so the dialog looks and
            behaves identically to the DP dashboard. The org-scoped facilityId is passed in;
            the locked PCC/identity fields render disabled exactly as on the DP shell. */}
        <FacilitySettings
          open={this.state.modalStatus}
          facilityId={this.state.editingFacilityId}
          onClose={this.modalClose}
          onSaved={() => this.fetchFacilities(this.state.searchKeyword)}
        />

        <GridContainer>
          <Card className={"advert-cover"}>
            <GridItem xs={12} sm={12} md={12}>
              <CardHeader
                color="success"
                className={"cusCardheader cusCardTitle"}
              >
                <h4>Facilities</h4>
              </CardHeader>
            </GridItem>
            <GridItem xs={12} sm={12} md={12}>
              <div className="search-btn-cover">
                <input
                  type="text"
                  title="Search facilities"
                  placeholder="Search facilities"
                  className="search-btn"
                  onChange={this.search}
                />
                <i className="material-icons">search</i>
              </div>
            </GridItem>
            <div className={"custom-container"}>
              <div className="tableResponsive">
                <Table>
                  <TableHead>
                    <TableRow>
                      {headCells.map((headCell) => (
                        <TableCell key={headCell.id} align="left">
                          {headCell.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.state.rows.length ? (
                      this.state.rows.map((f) => (
                        <TableRow key={f.facilityId}>
                          <TableCell>{f.facilityName}</TableCell>
                          <TableCell>{f.address}</TableCell>
                          <TableCell>
                            {f.status ? "Active" : "Inactive"}
                          </TableCell>
                          <TableCell>{f.activeUserCount}</TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {/* Managers are not allowed to edit facility settings — edit icon hidden.
                              <span
                                className="edit-action"
                                onClick={() => this.modalOpen(f.facilityId)}
                              >
                                <Icon
                                  className="fa fa-pencil-square-o"
                                  aria-hidden="true"
                                />
                              </span>
                              */}
                              <button
                                className="pointer btn1"
                                style={{ marginLeft: 8, whiteSpace: "nowrap" }}
                                onClick={() => this.openDashboard(f)}
                              >
                                ↗ Open dashboard
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="5">
                          <div className="not-found-text">
                            No facilities in your organization.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Card>
        </GridContainer>
      </div>
    );
  }
}

export default OrgFacilityList;
